import { LogoLoader } from "@/components"
import { ServerError } from "@/components/ServerError/ServerError"
import { useStores } from "@/stores"
import "@/styles/index.css"
import {
    AddressesProvider,
    AdpageBody,
    CartProvider,
    LastListerProvider,
    LayoutErrorBoundary,
    LocationProvider,
    MeProvider,
    PendingProvider,
    PrefetchData,
    PrefetchProvider,
    StatsigProvider,
} from "@jog/react-components"
import { PrismicPreview } from "@prismicio/next"
import { observer } from "mobx-react"
import { SessionProvider } from "next-auth/react"
import type { AppProps } from "next/app"
import Script from "next/script"
import { PropsWithChildren } from "react"
import { StoresProvider } from "stores/StoresProvider"

const CartProviderWrapper = observer(function CartProviderWrapper({ children }: PropsWithChildren) {
    const { cartStore } = useStores()
    return <CartProvider cartStore={cartStore}>{children}</CartProvider>
})

function AppContainer({ loader, children }: PropsWithChildren<{ loader: Partial<PrefetchData> }>) {
    return (
        <MeProvider>
            <AddressesProvider>
                <PrefetchProvider data={loader}>
                    {process.env.NEXT_PUBLIC_SQUEEZELY_ENABLE && (
                        <Script defer id="squeezely-worker" src="/squeezely-worker.js" />
                    )}
                    <LastListerProvider>
                        <StoresProvider>
                            <AdpageBody>
                                <LocationProvider>
                                    <PrismicPreview
                                        updatePreviewURL="/api/preview"
                                        exitPreviewURL="/api/exit-preview"
                                        repositoryName={process.env.NEXT_PUBLIC_PRISMIC_REPO}>
                                        <PendingProvider Pending={LogoLoader}>
                                            <CartProviderWrapper>
                                                <StatsigProvider>{children}</StatsigProvider>
                                            </CartProviderWrapper>
                                        </PendingProvider>
                                    </PrismicPreview>
                                </LocationProvider>
                            </AdpageBody>
                        </StoresProvider>
                    </LastListerProvider>
                </PrefetchProvider>
            </AddressesProvider>
        </MeProvider>
    )
}

export default function App({ pageProps, Component }: AppProps<{ loader: Partial<PrefetchData> }>) {
    if (pageProps.loader) {
        return (
            <LayoutErrorBoundary Fallback={() => <ServerError from="_app" />}>
                <SessionProvider>
                    <AppContainer loader={pageProps.loader}>
                        <Component {...pageProps} />
                    </AppContainer>
                </SessionProvider>
            </LayoutErrorBoundary>
        )
    }
    return <ServerError from="_app" />
}
